// COM-640: pure transitions for the All Articles page's scanning indicator
// (ScanIndicator.tsx), driven from SourcesContext.tsx.
//
// `started` fires the moment the client's OWN `addSource` call is made -
// OPTIMISTICALLY, before the server has confirmed anything - because
// AddSourceDialog closes immediately on submit with no other visibility
// into the round trip. `confirmed` fires on the `sourceAdded` that answers
// THIS call's own submission (backend/src/index.ts's `addSource` handler:
// `sourceAdded` = "the row exists, the scan is now running"). A scan
// `finished`s once the source's own status - reported by a later
// `sourcesLoaded` - reaches a terminal state. `failed` fires when the add
// itself is refused (a duplicate URL, COM-639's 100-source cap) or the
// submission-phase safety net times out. `timedOut` fires when the
// COMPLETION-phase safety net gives up while the scan is still confirmed and
// running server-side (see `WatchingScanState` below). `abandonAll` fires on
// a disconnect, a project switch, or an in-band server error this client has
// no way to correlate to one specific scan (see SourcesContext.tsx's
// `error` listener) - see that event's own doc comment for why it targets
// EVERYTHING rather than one scan.
//
// Fix wave (COM-640, whole-branch review, Critical 2 + 3): this module used
// to track exactly ONE scan (`ScanState` was a single tagged union:
// `idle | scanning | watching`). That collapsed under concurrency two ways,
// both reachable by submitting a second add before the first scan (already
// past `sourceAdded`, still running) finishes - AddSourceDialog closes as
// soon as `sourceAdded` arrives, well before the scan itself completes, so
// nothing in the UI actually prevents a second submission while the first is
// still in flight:
//   - Critical 2: `started` was a no-op while the single state wasn't idle,
//     so scan B was never tracked at all - its `confirmed` was then rejected
//     too (the state's `url` still held scan A's), so B's completion never
//     bumped `sourceScanCompletedCount`: B's articles never appeared without
//     a manual reload, and the Sources dropdown never gained a checkbox for
//     it.
//   - Critical 3: SourcesContext's single shared timer meant arming scan B's
//     submission-phase timeout cleared scan A's completion-phase timeout (or
//     vice versa) - whichever scan's timer lost the race could see its
//     `sourceAdded` silently dropped (see that module's own history for the
//     exact unclosable-dialog sequence this produced).
//
// Fixed by modelling a whole SET of concurrent scans instead of one -
// `ScanState` is now a map keyed by the submitted url (see `ScanState`'s own
// doc comment for why url, not source id, is the right key). This also
// matches the design more closely than the single-scan model ever did:
// Figma's own indicator copy is "Scanning N sources" - concurrent adds now
// naturally render "Scanning 2 sources" via `scanningCount` below instead of
// needing a special case that never existed for N > 1.
export type ScanPhase = 'scanning' | 'watching';

export interface ScanEntry {
  /** The url THIS scan was submitted with - duplicated onto the value (it's
   * also the map key `ScanState` stores it under) purely so a caller already
   * holding one `ScanEntry` doesn't need the key back to identify it. */
  url: string;
  /** The id `sourceAdded` reported for this scan's own row - `null` until
   * `confirmed` arrives. Needed to recognise, in a later `sourcesLoaded`,
   * which source's status means "this scan finished". */
  sourceId: string | null;
  /** `scanning`: the spinner is showing for this scan. `watching`: the
   * completion-phase safety net (`ADD_SOURCE_SCAN_TIMEOUT_MS`,
   * SourcesContext.tsx) gave up on THIS scan specifically - the spinner for
   * it is gone, but its identity is still under observation so a late
   * terminal `sourcesLoaded` still completes it instead of being silently
   * dropped. */
  phase: ScanPhase;
}

// Keying choice: the submitted URL, not the eventual source id.
//
// A scan's url is known from the moment `addSource` is called - before the
// server has replied at all - which is exactly when `started` needs to add
// an entry so the spinner/count reflects an optimistic add immediately (the
// whole reason `started` fires before any round trip, see this module's top
// comment). The source id isn't known until `confirmed` (`sourceAdded`)
// arrives, which can be seconds later or never (a refusal). Keying by
// source id instead would leave every not-yet-confirmed scan without a key
// to store it under at all. Url stays valid as the key for a scan's WHOLE
// lifecycle (submission through completion) since `confirmed`/`timedOut`/
// `finished` all update or remove the SAME entry in place rather than
// re-keying it once the source id becomes known.
//
// Two ENTRIES can never collide on this key - `started`'s own no-op-on-
// collision case below covers that - but re-review (whole-branch review,
// blocker) found that this does NOT mean a same-url resubmission is
// harmless. AddSourceDialog closes the instant `sourceAdded` arrives,
// minutes before a slow scan actually finishes, so a user who doesn't see
// the source show up naturally re-enters the SAME url while the first scan
// is still running server-side. The backend's duplicate-URL check
// (`getSourceByUrlAndProject`, backend/src/index.ts) then refuses the
// second call - and that refusal is exactly what used to do the damage: it
// drove `failed` for a url whose entry actually belonged to the FIRST,
// still-running scan (already confirmed, with its own `sourceId`), and
// `failed` used to delete whatever sat under that key unconditionally. The
// `failed` case below now only ever removes an entry it can actually own -
// see its own comment for why `sourceId === null` is the exact ownership
// test. (SourcesContext.tsx's per-url timer had the same shaped bug for the
// SAME reason - see `scanTimersRef`'s doc comment there.)
export type ScanState = Record<string, ScanEntry>;

export const EMPTY_SCAN_STATE: ScanState = {};

export type ScanEvent =
  | { type: 'started'; url: string }
  | { type: 'confirmed'; url: string; sourceId: string }
  | { type: 'timedOut'; url: string }
  | { type: 'finished'; url: string }
  | { type: 'failed'; url: string }
  // No url: fired when NOTHING can identify which scan(s) it applies to
  // (disconnect, project switch, an unattributable in-band server error) -
  // see SourcesContext.tsx's `rejectPendingAddSource`/`error` listener.
  | { type: 'abandonAll' };

export function addSourceScanReducer(state: ScanState, event: ScanEvent): ScanState {
  switch (event.type) {
    case 'started': {
      if (state[event.url]) {
        // A scan for this exact url is already tracked - see `ScanState`'s
        // own comment on why this is effectively unreachable via the real
        // UI (the backend's duplicate-URL check, or the dialog's own
        // modal/singleton submission flow). Not clobbering it either way:
        // whichever scan is already running for this url keeps its
        // (possibly already-confirmed) identity.
        return state;
      }
      return { ...state, [event.url]: { url: event.url, sourceId: null, phase: 'scanning' } };
    }
    case 'confirmed': {
      const entry = state[event.url];
      // Ignore a confirmation that isn't for a scan THIS map is tracking, or
      // one that's already past the point of being confirmable (`watching` -
      // `confirmed` only ever fires once per scan, right after `started`).
      // Mirrors the single-scan model's original collision guard: a
      // `sourceAdded` for a url nothing here submitted (source discovery's
      // own, a completely different url) finds no entry and is a no-op.
      if (!entry || entry.phase !== 'scanning' || entry.sourceId !== null) {
        return state;
      }
      return { ...state, [event.url]: { ...entry, sourceId: event.sourceId } };
    }
    case 'timedOut': {
      // Only a confirmed `scanning` entry (a `sourceId` to keep watching) for
      // THIS url can time out into `watching` - every OTHER concurrent
      // scan's own entry is untouched, since each scan's completion-phase
      // timer is armed and fires independently (SourcesContext.tsx's
      // per-url timer map).
      const entry = state[event.url];
      if (!entry || entry.phase !== 'scanning' || entry.sourceId === null) {
        return state;
      }
      return { ...state, [event.url]: { ...entry, phase: 'watching' } };
    }
    case 'finished': {
      // Removes exactly the one scan named by `event.url`, leaving every
      // other concurrent scan's entry untouched - the fix for Critical 2's
      // "a second add's completion must not disturb the first scan's own
      // tracked state" (and vice versa). `finished` only ever fires for an
      // entry `completedScanUrls` (below) picked out by ITS OWN `sourceId`
      // reaching a terminal status, so there's no ownership question here
      // the way there is for `failed` below - the entry named by the url is
      // always the one that just completed.
      if (!(event.url in state)) {
        return state;
      }
      const next = { ...state };
      delete next[event.url];
      return next;
    }
    case 'failed': {
      // Fix wave (re-review, blocker): `failed` is only ever dispatched
      // from `pendingAddSourceRef.reject` (SourcesContext.tsx) - the
      // SUBMISSION-phase rejection path, before a `sourceAdded` for this
      // call has ever arrived. A CONFIRMED entry (`sourceId !== null`) under
      // this same url key can therefore never be the entry this particular
      // `failed` belongs to - it can only be an EARLIER, still-running scan
      // for the same url that a same-url resubmission collided with (see
      // `ScanState`'s own comment above for the full reproduction: dialog
      // closes on `sourceAdded`, user re-enters the same url, backend
      // refuses the duplicate, and the refusal used to delete the first
      // scan's own confirmed entry here). Only remove an entry this event
      // actually owns - one that never got confirmed - so a refusal for a
      // resubmitted url leaves the real, in-flight scan's tracking, timer,
      // and eventual completion credit untouched.
      const entry = state[event.url];
      if (!entry || entry.sourceId !== null) {
        return state;
      }
      const next = { ...state };
      delete next[event.url];
      return next;
    }
    case 'abandonAll':
      // A disconnect, a project switch, or a server error this client
      // cannot attribute to one specific scan (see this type's own doc
      // comment) - nothing left in the map can be trusted, whatever phase
      // each entry was in.
      return Object.keys(state).length === 0 ? state : EMPTY_SCAN_STATE;
    default:
      return state;
  }
}

/** How many scans are currently showing the visible spinner - `watching`
 * scans (the spinner already gave up for them specifically) are excluded,
 * exactly like the old single-scan model's `isAddSourceScanning` excluded
 * `watching`. Drives `ScanIndicator`'s "Scanning N source(s)" copy (Figma
 * 3142:29000) - N was always 1 in the single-scan model; concurrency makes
 * this a real count instead of a boolean's stand-in. */
export function scanningCount(state: ScanState): number {
  let count = 0;
  for (const entry of Object.values(state)) {
    if (entry.phase === 'scanning') count += 1;
  }
  return count;
}

/** The subset of a `sourcesLoaded` payload's per-source fields
 * `completedScanUrls` needs - deliberately narrow (not the full `Source`
 * type) so callers don't have to construct one just to check this. */
export interface ScannedSourceStatus {
  id: string;
  status: string;
}

// Verified in `sourcesService.ts`/`dbService.ts`: `completed` (no relevant
// articles found, or articles saved successfully) and `error` (scrape/LLM
// failure, `markSourceProcessingFailed`) are the only two statuses
// `processContentAsync` ever writes, and it always writes exactly one of
// them before returning - there is no third outcome. `pending`/`in_progress`
// are non-terminal (a fresh/scheduler-created row, or a scan still running).
const TERMINAL_SOURCE_STATUSES = new Set(['completed', 'error']);

/**
 * Which of `state`'s tracked scans (`scanning` OR `watching` - completion
 * must still be recognised after the visible timeout gives up, see
 * `ScanEntry.phase`'s doc comment) have their own source id reaching a
 * terminal status in `sources` (a raw `sourcesLoaded` payload). Returns the
 * scans' own url keys - zero, one, or, since concurrent scans are now
 * trackable at all, possibly more than one if several finish inside the
 * same `sourcesLoaded` reply.
 *
 * A `sourcesLoaded` triggered by something else entirely (a
 * dropdown-triggered `getSources`, a concurrent discovery `sourcesLoaded`)
 * either won't carry a given scan's source id at all, or will carry it still
 * `in_progress` - both correctly report "not complete yet" for that scan
 * rather than being misread as "a reply arrived, so it must be mine".
 */
export function completedScanUrls(state: ScanState, sources: ScannedSourceStatus[]): string[] {
  const statusById = new Map(sources.map((source) => [source.id, source.status]));
  const finished: string[] = [];
  for (const entry of Object.values(state)) {
    if (entry.sourceId === null) continue;
    const status = statusById.get(entry.sourceId);
    if (status !== undefined && TERMINAL_SOURCE_STATUSES.has(status)) {
      finished.push(entry.url);
    }
  }
  return finished;
}
